import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import apiService from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  register: (userData: any) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false); // 初始化为false，允许立即渲染

  useEffect(() => {
    // 延迟检查token，优先加载页面内容
    const timer = setTimeout(() => {
      checkAuth();
    }, 100); // 延迟100ms检查，让页面先渲染
    
    return () => clearTimeout(timer);
  }, []);

  const checkAuth = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (token) {
        console.log('🔍 检查token有效性...');
        const response = await apiService.get<User>('/auth/me');
        if (response.success && response.data) {
          console.log('✅ token有效，用户已登录');
          setUser(response.data);
        } else {
          console.log('❌ token无效，清除token');
          localStorage.removeItem('token');
          setUser(null);
        }
      } else {
        console.log('🔍 未找到token，允许匿名访问');
        setUser(null);
      }
    } catch (error) {
      console.log('❌ token验证失败，清除token');
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await apiService.post('/auth/login', {
        email,
        password,
      });

      if (response.success && response.data) {
        localStorage.setItem('token', response.data.token);
        setUser(response.data.user);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const register = async (userData: any): Promise<boolean> => {
    try {
      console.log('AuthContext: 开始注册请求');
      const response = await apiService.post('/auth/register', userData);
      console.log('AuthContext: 注册响应:', response);
      
      if (response.success && response.data) {
        localStorage.setItem('token', response.data.token);
        setUser(response.data.user);
        return true;
      }
      console.log('AuthContext: 注册失败，响应:', response);
      return false;
    } catch (error: any) {
      console.error('AuthContext: 注册异常:', error);
      if (error.response) {
        console.error('AuthContext: 错误状态码:', error.response.status);
        console.error('AuthContext: 错误响应数据:', JSON.stringify(error.response.data, null, 2));
        console.error('AuthContext: 完整错误对象:', JSON.stringify(error.response, null, 2));
      } else if (error.request) {
        console.error('AuthContext: 请求错误:', error.request);
      } else {
        console.error('AuthContext: 其他错误:', error.message);
      }
      return false;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    register,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 